import React, { Suspense, useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { BrowserTracing } from "@sentry/tracing";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { ConfigProvider, App as AntApp } from "antd";
import { createCache, StyleProvider } from "@ant-design/cssinjs";
import { store } from "./redux/store";
import GlobalPreloader from "./components/_auth/GlobalPreloader";
import App from "./pages/App";
import "./i18n";
import "./styles/index.scss"; // SCSS custom
import "antd/dist/reset.css"; //// AntD v5
import { clearTablesFiltersLocalStorage } from "./utils/common";
import { StaticFunctionsBridge } from "./utils/appStaticFunctions";
import ErrorBoundary from "./components/ErrorBoundary";

// -------------------- Sentry init --------------------
Sentry.init({
  allowUrls: ["teampulse.it", "teampulse.co"],
  dsn: "https://8827aa43eb19fc4afaeb41be1fb78f14@o4511545721683968.ingest.de.sentry.io/4511545751044176",
  integrations: [new BrowserTracing()],
  // filter out UnhandledRejection errors that have no information
  beforeSend: (event) => {
    if (
      event?.exception?.values?.length === 1 &&
      event.exception.values[0].type === "UnhandledRejection" &&
      event.exception.values[0].value === "Non-Error promise rejection captured with value: "
    ) {
      return null;
    }

    // Outlook's link scanner opens the pages received by email inside its own wrapper and reports this
    // rejection from the script it injects. It does not come from the application code, so it is dropped.
    if (event?.exception?.values?.[0]?.value?.includes("Object Not Found Matching Id")) {
      return null;
    }

    // After a deploy, old browser tabs request JS chunks that no longer exist. The server returns HTML
    // (index.html or a 404 page) and the browser fails to parse it as JavaScript. loadable.js already
    // handles this by reloading the page, so the Sentry report is noise.
    if (
      event?.exception?.values?.[0]?.type === "SyntaxError" &&
      event?.exception?.values?.[0]?.value?.includes("Unexpected token '<'")
    ) {
      return null;
    }

    // TinyMCE internal error when selection/range operations run on a node that was removed from the DOM.
    // This is a race condition during navigation or editor teardown - does not affect user experience.
    if (event?.exception?.values?.[0]?.value?.includes("Node cannot be null or undefined")) {
      return null;
    }

    // TinyMCE IndexSizeError when cursor/selection Range targets a DOM position that no longer exists.
    // Same race condition as above - happens during navigation or modal close while editor is active.
    if (
      event?.exception?.values?.[0]?.type === "IndexSizeError" ||
      event?.exception?.values?.[0]?.value?.includes("setStart") ||
      event?.exception?.values?.[0]?.value?.includes("setEnd")
    ) {
      return null;
    }

    // React DOM error when trying to remove a node that was already modified by browser extensions
    // (Grammarly, ad blockers, translation tools). Benign and outside application control.
    if (event?.exception?.values?.[0]?.value?.includes("The node to be removed is not a child of this node")) {
      return null;
    }

    // HTTP 422 validation errors are expected user input errors, not bugs. The form shows the errors to the user.
    if (event?.exception?.values?.[0]?.value?.includes("Request failed with status code 422")) {
      return null;
    }

    return event;
  },
  // Errors are captured at default sampleRate: 1.0 (unchanged). tracesSampler affects only performance spans.
  tracesSampler: (samplingContext) => {
    const name = samplingContext?.transactionContext?.name || "";
    if (name.includes("colors.css")) return 0;
    if (name.includes("notifications") && name.includes("GET")) return 0.01;
    if (name.includes("users/me") || name.includes("auth/refresh")) return 0.05;
    return 0.1;
  }
});

// -------------------- Create root --------------------
const root = createRoot(document.getElementById("root"));

const AppWithTheme = () => {
  const [token, setToken] = useState({
    colorPrimary: "#87CA96",
    colorLink: "#336BB4",
    borderRadius: 2
  });

  useEffect(() => {
    const interval = setInterval(() => {
      const primary = getComputedStyle(document.documentElement).getPropertyValue("--color-primary").trim();
      const link = getComputedStyle(document.documentElement).getPropertyValue("--color-link").trim();

      if (primary) {
        setToken({
          colorPrimary: primary,
          colorLink: link || primary,
          borderRadius: 2
        });
        clearInterval(interval);
      }
    }, 100);
    return () => clearInterval(interval);
  }, []);

  return (
    <ConfigProvider theme={{ token }} hashPriority="high">
      <AntApp>
        <StaticFunctionsBridge />
        <App />
      </AntApp>
    </ConfigProvider>
  );
};

const cache = createCache();
const insertionPoint = document.getElementById("antd-insertion-point");

root.render(
  <ErrorBoundary>
    <Suspense fallback={<GlobalPreloader />}>
      <Provider store={store}>
        <StyleProvider cache={cache} insertionPoint={insertionPoint}>
          <AppWithTheme />
        </StyleProvider>
      </Provider>
    </Suspense>
  </ErrorBoundary>
);

// clear tables filters from localStorage
clearTablesFiltersLocalStorage();
